import { useApolloClient } from '@apollo/client';
import { WEB_URL } from '@brand/const';
import { Box, ClickAwayListener, Divider, Popper, PopperProps, Stack, Typography } from '@mui/material';
import { AuthContext } from 'common/providers/AuthContextProvider/AuthContextProvider';
import { Role, useCurrentUserQuery } from 'generated/graphql';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { isProductionLikeEnvironment } from '../../../environment';
import { styles } from './UserHeaderMenuPopper.styles';

interface UserHeaderMenuPopper {
  open: boolean;
  onClose: () => void;
  anchorElement: PopperProps['anchorEl'];
}

export const UserHeaderMenuPopper: React.FC<UserHeaderMenuPopper> = ({ anchorElement, onClose, open }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const client = useApolloClient();

  const { isAuthenticated, signOut, userData } = useContext(AuthContext);
  const { data: currentUserData } = useCurrentUserQuery({ skip: !isAuthenticated });

  const handleLogout = async () => {
    const redirectUrl = isProductionLikeEnvironment ? WEB_URL : '/';
    await client.clearStore();
    await signOut(redirectUrl);
  };

  const handleSettings = () => {
    navigate('/settings');
  };

  const handleAdmin = () => {
    navigate('/admin');
  };

  const staffRoles = [Role.RoleAdmin, Role.RoleFinancialAdvisor, Role.RoleScout];

  const hasAnyRole = (allowedRoles: Role[]) =>
    isAuthenticated && currentUserData?.user?.roles?.some((role) => allowedRoles.includes(role));

  return (
    <Popper anchorEl={anchorElement} disablePortal open={open} placement="bottom-end">
      <ClickAwayListener onClickAway={onClose}>
        <Box sx={styles.menuWrapper}>
          <Typography variant="bodyStrongS">{userData?.user.attributes.email ?? '-'}</Typography>
          <Divider sx={styles.divider} />
          <Stack>
            {hasAnyRole(staffRoles) && (
              <Box onClick={handleAdmin} sx={styles.button}>
                <Typography variant="bodyS">{t('userHeaderMenu.adminButton')}</Typography>
              </Box>
            )}
            <Box onClick={handleSettings} sx={styles.button}>
              <Typography variant="bodyS">{t('userHeaderMenu.settingsButton')}</Typography>
            </Box>
            <Box onClick={handleLogout} sx={styles.button}>
              <Typography variant="bodyS">{t('userHeaderMenu.logoutButton')}</Typography>
            </Box>
          </Stack>
        </Box>
      </ClickAwayListener>
    </Popper>
  );
};
